import React from 'react';
import LightboxImage from '@reverbdotcom/commons/src/components/lightbox_image';
import { getVideoSlug } from './item2_video';
import I18n from 'i18n-js';
import { LISTING_STATES } from '@reverbdotcom/commons/src/constants';
import { RCTextWithIcon } from '@reverbdotcom/cadence/components';
import { ArrowRightIcon } from '@reverbdotcom/cadence/icons/react';
import CoreLink from '@reverbdotcom/commons/src/components/core_link';
import { MParticleEventName } from '@reverbdotcom/commons/src/elog/mparticle_types';
import { getSimilarListingPageRoute } from './shared/url_helpers';
import { gql } from '@reverbdotcom/commons/src/gql';
import { ItemImageFieldsFragment } from '@reverbdotcom/commons/src/gql/graphql';

interface Props {
  listing: ItemImageFieldsFragment;
  hideThumbs?: boolean;
  hidePrimary?: boolean;
}

export function Item2Image({
  listing,
  hideThumbs = false,
  hidePrimary = false,
}: Props) {
  const large = getImages(listing, 'largeImages');
  const thumb = getImages(listing, 'cardSquareImages');
  const full = getImages(listing, 'supersizeImages');

  return (
    <>
      {!isLive(listing) && renderStatusIndicatorImageBanner(listing)}
      <LightboxImage
        data-lightbox-image
        imageURLs={large}
        thumbURLs={thumb}
        fullsizeURLs={full}
        imageAlts={getImageAlts(listing.title, large)}
        persistKeystrokes
        useAspectOverride
        withControls
        hideThumbs={hideThumbs}
        hidePrimary={hidePrimary}
        eagerLoadPrimaryImage
      />
    </>
  );
}

export const ItemImageFragment = gql(`
  fragment ItemImageFields on Listing {
    _id
    title
    state
    largeImages: images(input: { type: "Product", scope: "photos", transform: "large", count: 25}) {
      source
    }
    supersizeImages: images(input: { type: "Product", scope: "photos", transform: "supersize", count: 25 }) {
      source
    }
    cardSquareImages: images(input: { type: "Product", scope: "photos", transform: "card_square", count: 25}) {
      source
    }
    video {
      youtubeVideoId
      empty
    }
    csp {
      _id
      videos {
        videoLinks {
          youtubeVideoId
        }
      }
    }
  }
`);

export function getImages(listing, transform) {
  const images = listing[transform];
  const video = getVideoSlug(listing);
  let sources = [];
  if (images) {
    sources = images.map(img => img.source);
  }

  if (video) sources.push(video);

  return sources;
}

function getImageAlts(title, images) {
  return images.map((_, index) => (
    I18n.t('discovery.item2Layout.imageAlt', { title, index: index + 1 })
  ));
}

function isLive(listing): boolean {
  return listing.state === LISTING_STATES.LIVE;
}

function renderStatusIndicatorImageBanner(listing) {
  let statusIndicatorText;
  switch (listing.state) {
    case LISTING_STATES.ENDED:
      statusIndicatorText = I18n.t('discovery.item2Image.statusIndicatorBannerEnded');
      break;
    case LISTING_STATES.ORDERED:
    case LISTING_STATES.SOLD_OUT:
      statusIndicatorText = I18n.t('discovery.item2Image.statusIndicatorBannerSold');
      break;
    case LISTING_STATES.SELLER_UNAVAILABLE:
      statusIndicatorText = I18n.t('discovery.item2Image.statusIndicatorBannerUnavailable');
      break;
    default:
      return null;
  }

  return (
    <div className="item2-image__status-indicator-image-banner">
      <CoreLink
        to={getSimilarListingPageRoute(listing.id, listing.make, listing.model)}
        className="color-primary"
        clickEvent={
          {
            componentName: 'EndedListingImageBanner',
            eventName: MParticleEventName.ClickedComponent,
            listingId: listing.id,
          }
        }
      >
        <div>
          <RCTextWithIcon placement="right" svgComponent={ArrowRightIcon}>
            {statusIndicatorText}
          </RCTextWithIcon>
        </div>
      </CoreLink>
    </div>
  );
}
